import ClearIcon from "@assets/images/Clear.svg";
import {
  FormControl,
  FormHelperText,
  IconButton,
  InputAdornment,
  OutlinedInput,
  TextField
} from "@material-ui/core";
import { makeStyles } from "@saleor/macaw-ui";
import cls from "classnames";
import React, { useEffect, useRef, useState } from "react";

import MyTypography from "../MyTypography";
import { ISelectFieldProps } from "../SingleSelectField";

interface SingleFieldProps extends ISelectFieldProps {
  value?: string;
  isReset?: boolean;
  multiline?: boolean;
  size?: Partial<32 | 40 | 48 | 56>;
  className?: string;
  readOnly?: boolean;
  required?: boolean;
  labelFocus?: string;
  maxLengthProps?: number;
  textUpperCase?: boolean;
  regex?: RegExp;
  id?: string;
  testId?: string;
  showClearIcon?: boolean;
  onKeyPress?(event: any);
  onPaste?(event: any);
  onMouseEnter?(event: any);
}
const useStyles = ({ showClearIcon }) =>
  makeStyles(
    theme => ({
      error: {
        color: theme.palette.error.main
      },
      root: {
        width: "100%",
        ...(showClearIcon && {
          paddingRight: "8px !important",
          "&:hover .clear-icon": {
            opacity: 1,
            visibility: "visible"
          }
        })
      },
      clearIcon: {
        ...(showClearIcon && {
          padding: 4,
          color: theme.palette.grey[500],
          border: "unset",
          opacity: 0,
          visibility: "hidden",
          transition: "opacity 0.2s ease, visibility 0.2s ease",
          "&:hover": {
            color: theme.palette.grey[700]
          }
        })
      }
    }),
    { name: "SingleTextField" }
  );
export const SingleTextField: React.FC<SingleFieldProps> = props => {
  const {
    disabled,
    label,
    value,
    onChange,
    onBlur,
    onKeyPress,
    onPaste,
    name,
    hint,
    placeholder,
    error,
    multiline,
    className,
    readOnly,
    required,
    labelFocus,
    regex,
    maxLengthProps = 255,
    onMouseEnter,
    textUpperCase,
    id,
    testId,
    InputProps,
    showClearIcon = false
  } = props;
  const classes = useStyles({ showClearIcon })();
  const [textValue, setValue] = useState(value);
  const isChanged = useRef(false);

  const handleChange = event => {
    isChanged.current = true;
    const value = event.target.value;
    if (regex && (!regex.test(value) || value.length > maxLengthProps)) {
      return;
    }
    if (value.length <= maxLengthProps) {
      setValue(value);
    }
    if (textUpperCase && value.length <= maxLengthProps) {
      setValue(value.toUpperCase());
    }
  };

  const handleClear = () => {
    isChanged.current = true;
    setValue("");
  };

  useEffect(() => {
    if (isChanged.current) {
      onChange({
        target: {
          name,
          value: textValue
        }
      });
    }
  }, [textValue]);

  useEffect(() => {
    if (!value && isChanged.current) {
      setValue("");
      return;
    }
    if (!isChanged.current) {
      setValue(value);
    }
  }, [value]);

  return (
    <FormControl className={classes.root} error={error} disabled={disabled}>
      {label ? (
        <MyTypography variant="body3">
          {label} {required && <span className={classes.error}>*</span>}
        </MyTypography>
      ) : null}
      {!labelFocus ? (
        <OutlinedInput
          aria-describedby="outlined-weight-helper-text"
          data-input-type="single-text-field"
          autoComplete="off"
          error={error}
          value={textValue}
          name={name}
          id={id}
          data-testid={testId}
          onMouseEnter={onMouseEnter}
          placeholder={placeholder}
          onChange={handleChange}
          onBlur={onBlur}
          onKeyDown={onKeyPress}
          onPaste={onPaste}
          multiline={multiline}
          readOnly={readOnly}
          className={cls(className)}
          endAdornment={
            showClearIcon && textValue && !readOnly && !disabled ? (
              <InputAdornment position="end">
                <IconButton
                  className={`${classes.clearIcon} clear-icon`}
                  onClick={handleClear}
                  edge="end"
                  size="small"
                >
                  <img src={ClearIcon} alt="ClearIcon" />
                </IconButton>
              </InputAdornment>
            ) : null
          }
          inputProps={{
            ...InputProps?.inputProps
          }}
        />
      ) : (
        <TextField
          aria-describedby="outlined-weight-helper-text"
          data-input-type="single-text-field"
          autoComplete="off"
          error={error}
          value={value}
          name={name}
          id={id}
          data-testid={testId}
          onMouseEnter={onMouseEnter}
          label={labelFocus}
          placeholder={placeholder}
          onChange={handleChange}
          onBlur={onBlur}
          onKeyDown={onKeyPress}
          onPaste={onPaste}
          multiline={multiline}
          className={cls(className)}
          InputProps={{
            endAdornment:
              showClearIcon && textValue && !readOnly && !disabled ? (
                <InputAdornment position="end">
                  <IconButton
                    className={`${classes.clearIcon} clear-icon`}
                    onClick={handleClear}
                    edge="end"
                    size="small"
                  >
                    <img src={ClearIcon} alt="ClearIcon" />
                  </IconButton>
                </InputAdornment>
              ) : null,
            ...InputProps
          }}
          inputProps={{
            maxLength: maxLengthProps,
            ...InputProps?.inputProps
          }}
        />
      )}
      {hint && <FormHelperText>{hint}</FormHelperText>}
    </FormControl>
  );
};
export default SingleTextField;
